import { kpiDslName } from '../kpi-names';
import { splitKeyedName } from '../vocabulary/vocabulary';
import {
  CompatibilityCondition,
  CompatibilityRequirement,
  FILTER_OP_SHAPES,
  FieldPath,
  FilterExpr,
  FilterLiteral,
  FilterOp,
  FilterOpName,
  FilterPredicate,
  LabelLiteral,
  LabelLiterals,
  LabelRange,
  LabelText,
  LengthUnit,
  LiteralRange,
  MassUnit,
  RelativeDateKeyword,
  RelativeDateSpec,
  SelfRef,
  isFilterOpName,
  opArgument,
  opName,
  segmentName
} from '../wire';

export const RELATIVE_DATE_DSL: Record<RelativeDateKeyword, string> = {
  today: 'today',
  yesterday: 'yesterday',
  tomorrow: 'tomorrow',
  last7_days: 'last_7_days',
  last30_days: 'last_30_days',
  last3_months: 'last_3_months',
  last6_months: 'last_6_months',
  last_year: 'last_year',
  next7_days: 'next_7_days',
  next30_days: 'next_30_days',
  next3_months: 'next_3_months',
  next6_months: 'next_6_months',
  next_year: 'next_year'
};

const MASS_SUFFIX: Record<MassUnit, string> = { Gram: 'g', Kilogram: 'kg', Ton: 't' };
const LENGTH_SUFFIX: Record<LengthUnit, string> = {
  Millimeter: 'mm',
  Centimeter: 'cm',
  Meter: 'm',
  Kilometer: 'km'
};

/** The phrase each operator prints with; label operators print it after `label "<key>"`. */
export const OP_PHRASES: Record<FilterOpName, string> = {
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  equals: '==',
  not_equals: '!=',
  greater_than: '>',
  greater_than_or_equal: '>=',
  less_than: '<',
  less_than_or_equal: '<=',
  between: 'between',
  is_any_of: 'in',
  is_none_of: 'not in',
  contains_all: 'contains all',
  not_contains_all: 'not contains all',
  relative_date: 'is',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  matches_regex: 'matches',
  has_tag: 'has tag',
  not_has_tag: 'has no tag',
  has_any_tag: 'has any tag',
  has_all_tags: 'has all tags',
  has_none_of_tags: 'has none of tags',
  has_label: 'has label',
  not_has_label: 'has no label',
  label_equals: '==',
  label_not_equals: '!=',
  label_in: 'in',
  label_not_in: 'not in',
  label_greater_than: '>',
  label_greater_than_or_equal: '>=',
  label_less_than: '<',
  label_less_than_or_equal: '<=',
  label_between: 'between',
  label_contains: 'contains',
  label_starts_with: 'starts with',
  label_ends_with: 'ends with',
  label_matches_regex: 'matches',
  label_references: 'references',
  label_references_any_of: 'references any',
  label_references_none_of: 'references none'
};

export function printStr(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Rust's `{:?}` for f64: always carries `.` or an exponent, so it never reads back as an integer. */
export function printNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e16 || abs < 1e-4))
    return value.toExponential().replace('e+', 'e');
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

/** The lexer reads a leading `P` before a digit or `T` as a duration, so such a name is quoted. */
function isBareName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !/^P[0-9T]/.test(name) && name !== 'not';
}

export function printSegmentName(name: string, forceQuote = false): string {
  if (!forceQuote && isBareName(name)) return name;
  return `\`${name.replace(/\\/g, '\\\\').replace(/`/g, '\\`')}\``;
}

/** A KPI field as `kpi.<name>[<key>]` when its name and key read back bare, else as any segment. */
export function printFieldName(name: string, forceQuote = false): string {
  const kpi = kpiDslName(name);
  if (kpi === null || forceQuote) return printSegmentName(name, forceQuote);
  const keyed = splitKeyedName(kpi);
  const bare =
    isBareName(keyed?.base ?? kpi) &&
    (!keyed || (isBareName(keyed.key) && keyed.key !== 'any' && keyed.key !== 'all'));
  return bare ? `kpi.${kpi}` : printSegmentName(name);
}

export function printPath(path: FieldPath, quoteLeadingLabel = false): string {
  return path.segments
    .map((segment, index) => {
      const raw = segmentName(segment);
      const name = printFieldName(
        raw,
        (quoteLeadingLabel && index === 0 && raw === 'label') ||
          // Followed by `.`, a bare `kpi` would read back as the start of a KPI name.
          (raw === 'kpi' && 'field' in segment && index < path.segments.length - 1)
      );
      return 'collection' in segment ? `${name}[${segment.collection.quantifier}]` : name;
    })
    .join('.');
}

function printSelfRef(ref: SelfRef): string {
  if ('label' in ref) return `self label ${printStr(ref.label)}`;
  // A leading segment named `label` would read back as `self label …`.
  return `self ${printPath(ref.field, true)}`;
}

function lookup<K extends string>(table: Record<K, string>, key: string): string | null {
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key as K] : null;
}

/** All the parts joined by spaces, or `null` when one of them cannot be spelled. */
function spaced(...parts: (string | null)[]): string | null {
  return parts.includes(null) ? null : parts.join(' ');
}

/** The DSL spelling of `literal`, or `null` for a kind or unit this build does not know. */
export function spellLiteral(literal: FilterLiteral): string | null {
  if (literal === 'null') return 'null';
  if (typeof literal !== 'object' || literal === null) return null;
  if ('number' in literal) return printNumber(literal.number);
  if ('integer' in literal) return String(literal.integer);
  if ('string' in literal) return printStr(literal.string);
  if ('bool' in literal) return String(literal.bool);
  if ('uid' in literal) return `uid(${printStr(literal.uid)})`;
  if ('uid_list' in literal) return `uids[${literal.uid_list.map(printStr).join(', ')}]`;
  if ('mass' in literal) {
    const suffix = lookup(MASS_SUFFIX, literal.mass.unit);
    return suffix === null ? null : `${literal.mass.value}${suffix}`;
  }
  if ('length' in literal) {
    const suffix = lookup(LENGTH_SUFFIX, literal.length.unit);
    return suffix === null ? null : `${literal.length.value}${suffix}`;
  }
  if ('duration' in literal) return literal.duration;
  if ('date' in literal) return literal.date;
  if ('date_time' in literal) return literal.date_time;
  if ('list' in literal) return spellLiterals(literal.list);
  if ('self' in literal) return printSelfRef(literal.self);
  return null;
}

/** For display outside the DSL; a literal this build cannot spell shows as stored. */
export function printLiteral(literal: FilterLiteral): string {
  return spellLiteral(literal) ?? JSON.stringify(literal);
}

function spellLiterals(items: FilterLiteral[]): string | null {
  const spelled = items.map(spellLiteral);
  return spelled.includes(null) ? null : `[${spelled.join(', ')}]`;
}

function spellRelativeDate(spec: RelativeDateSpec): string | null {
  if (typeof spec === 'string') return lookup(RELATIVE_DATE_DSL, spec);
  return 'within' in spec ? `within ${spec.within}` : null;
}

/** `null` for an operator, or an argument, this build cannot spell. */
function spellOp(op: FilterOp): string | null {
  const name = opName(op);
  if (!isFilterOpName(name)) return null;
  const phrase = OP_PHRASES[name];
  const arg = opArgument(op);
  switch (FILTER_OP_SHAPES[name]) {
    case 'none':
      return phrase;
    case 'literal':
      return spaced(phrase, spellLiteral(arg as FilterLiteral));
    case 'range': {
      const range = arg as LiteralRange;
      return spaced(phrase, spellLiteral(range.lo), '..', spellLiteral(range.hi));
    }
    case 'literals':
      return spaced(phrase, spellLiterals(arg as FilterLiteral[]));
    case 'relative_date':
      return spaced(phrase, spellRelativeDate(arg as RelativeDateSpec));
    case 'text':
      return `${phrase} ${printStr(arg as string)}`;
    case 'texts':
      return `${phrase} [${(arg as string[]).map(printStr).join(', ')}]`;
    case 'label_literal': {
      const value = arg as LabelLiteral;
      return spaced('label', printStr(value.label), phrase, spellLiteral(value.value));
    }
    case 'label_literals': {
      const value = arg as LabelLiterals;
      return spaced('label', printStr(value.label), phrase, spellLiterals(value.values));
    }
    case 'label_range': {
      const value = arg as LabelRange;
      return spaced(
        'label',
        printStr(value.label),
        phrase,
        spellLiteral(value.lo),
        '..',
        spellLiteral(value.hi)
      );
    }
    case 'label_text': {
      const value = arg as LabelText;
      return `label ${printStr(value.label)} ${phrase} ${printStr(value.value)}`;
    }
  }
}

/**
 * Grammar this build cannot spell (an operator, literal kind, unit or node the data layer added
 * since) prints as the stored JSON of its predicate or node. No DSL token starts with `{`, so text
 * holding it is refused as a whole rather than read back as a different filter.
 */
function printStored(node: object): string {
  return JSON.stringify(node);
}

export function printPredicate(predicate: FilterPredicate): string {
  const op = spellOp(predicate.op);
  return op === null ? printStored({ predicate }) : `${printPath(predicate.path)} ${op}`;
}

const OR = 0;
const AND = 1;
const UNARY = 2;
const ATOM = 3;

function precedenceOf(expr: FilterExpr): number {
  if ('and' in expr) return expr.and.length < 2 ? ATOM : AND;
  if ('or' in expr) return expr.or.length < 2 ? ATOM : OR;
  if ('not' in expr) return UNARY;
  return ATOM;
}

function printInfixOperand(expr: FilterExpr, parent: number): string {
  const text = printFilter(expr);
  return precedenceOf(expr) <= parent ? `(${text})` : text;
}

/** `expr` printed as one operand of an AND (parenthesised when it is itself an AND or an OR). */
export function printFilterOperand(expr: FilterExpr): string {
  return printInfixOperand(expr, AND);
}

export function printFilter(expr: FilterExpr): string {
  if ('and' in expr) {
    return expr.and.length >= 2
      ? expr.and.map((item) => printInfixOperand(item, AND)).join(' and ')
      : `all(${expr.and.map(printFilter).join(', ')})`;
  }
  if ('or' in expr) {
    return expr.or.length >= 2
      ? expr.or.map((item) => printInfixOperand(item, OR)).join(' or ')
      : `any(${expr.or.map(printFilter).join(', ')})`;
  }
  if ('not' in expr) {
    const text = printFilter(expr.not);
    return `not ${precedenceOf(expr.not) < UNARY ? `(${text})` : text}`;
  }
  if ('predicate' in expr) return printPredicate(expr.predicate);
  if ('association' in expr) {
    const association = expr.association;
    return `${printSegmentName(association.edge)}[${association.quantifier}](${printFilter(association.filter)})`;
  }
  return printStored(expr);
}

export function printCondition(condition: CompatibilityCondition): string {
  switch (condition.kind) {
    case 'context':
      return `context(${printFilter(condition.filter)})`;
    case 'route_position':
      return `position in [${condition.positions.join(', ')}]`;
    case 'service_time_window':
      return `service_window ${condition.start} .. ${condition.end}`;
  }
}

export function printRequirement(requirement: CompatibilityRequirement): string {
  let text = `${requirement.target_entity_type}: ${printFilter(requirement.filter)}`;
  if (requirement.violation_penalty != null) {
    text += ` penalty ${printNumber(requirement.violation_penalty)}`;
  }
  if (requirement.conditions?.length) {
    text += ` when ${requirement.conditions.map(printCondition).join(' and ')}`;
  }
  return text;
}
